import React from 'react';
import { Link } from 'react-router-dom';
import { Flag, Shield, Wrench, Anchor, CheckCircle, Award, Waves, Wind } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { products, pastProjects } from '../mock/coastalMockData';

const CoastalHome = () => {
  const featuredProducts = [
    ...products.usFlags.filter(p => p.bestseller).slice(0, 2),
    ...products.poles.filter(p => p.bestseller).slice(0, 2)
  ].slice(0, 4);

  return (
    <div>
      {/* Hero Section with American Flag Background */}
      <section className="relative overflow-hidden">
        {/* American Flag Overlooking Gulf Background */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1502566533923-170cf33bbcb8?w=1920&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        ></div>
        
        {/* Lighter Gradient Overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/30 to-blue-50/40"></div>
        
        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6">
              <span className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-2 rounded-full text-sm font-semibold">
                Gulf Coast's Premier Flag Source
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Marine-Grade Flags &
              <span className="block bg-gradient-to-r from-blue-700 to-cyan-600 bg-clip-text text-transparent">
                Hurricane-Resistant Poles
              </span>
            </h1>
            <p className="text-xl text-slate-700 mb-8 max-w-2xl mx-auto">
              Serving Santa Rosa Beach, Destin, Panama City, 30A and the entire Gulf Coast with premium flags and flagpoles engineered for coastal conditions.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8 py-6 text-lg">
                <Link to="/us-flags">Shop Coastal Flags</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="px-8 py-6 text-lg border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
                <Link to="/installation">Mobile Installation Service</Link>
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
              <div className="flex flex-col items-center">
                <Award className="w-10 h-10 text-blue-600 mb-2" />
                <div className="font-semibold text-slate-900">Marine Grade</div>
                <div className="text-sm text-slate-600">Quality</div>
              </div>
              <div className="flex flex-col items-center">
                <Wind className="w-10 h-10 text-cyan-600 mb-2" />
                <div className="font-semibold text-slate-900">Hurricane</div>
                <div className="text-sm text-slate-600">Resistant</div>
              </div>
              <div className="flex flex-col items-center">
                <Waves className="w-10 h-10 text-blue-600 mb-2" />
                <div className="font-semibold text-slate-900">Mobile</div>
                <div className="text-sm text-slate-600">Service</div>
              </div>
              <div className="flex flex-col items-center">
                <Flag className="w-10 h-10 text-cyan-600 mb-2" />
                <div className="font-semibold text-slate-900">Made in</div>
                <div className="text-sm text-slate-600">USA</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="relative py-20 overflow-hidden">
        {/* Subtle American flag background */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1563804846955-f87a66f0b20a?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-white via-red-50/30 to-blue-50/30"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Coastal Best Sellers</h2>
            <p className="text-xl text-slate-600">Marine-grade quality built for Gulf Coast conditions</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredProducts.map((product) => (
              <Card key={product.id} className="hover:shadow-xl transition-shadow duration-300 group border-2 border-transparent hover:border-blue-200">
                <div className="relative overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {product.bestseller && (
                    <span className="absolute top-4 right-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      Best Seller
                    </span>
                  )}
                </div>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-2 text-slate-900">{product.name}</h3>
                  <p className="text-slate-600 text-sm mb-4">{product.description}</p>
                  <div className="flex items-center justify-between">
                    {product.category === 'Poles' ? (
                      <span className="text-lg font-semibold text-slate-700">Call for Quote</span>
                    ) : (
                      <span className="text-2xl font-bold text-blue-600">${product.price}</span>
                    )}
                    <Button asChild size="sm" className="bg-slate-900 hover:bg-slate-800">
                      <Link to={product.category === 'Poles' ? '/installation' : `/${product.category.toLowerCase()}`}>
                        {product.category === 'Poles' ? 'Get Quote' : 'View Details'}
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
              <Link to="/us-flags">View All Products</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Coastal Categories */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Shop by Category</h2>
            <p className="text-xl text-slate-600">Premium products engineered for coastal living</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border-2 border-transparent hover:border-blue-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Flag className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold mb-2 text-slate-900">U.S. Flags</h3>
                <p className="text-slate-600 mb-6">Hurricane-resistant American flags for coastal display</p>
                <Button asChild className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700">
                  <Link to="/us-flags">Shop Now</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border-2 border-transparent hover:border-blue-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Anchor className="w-8 h-8 text-cyan-600" />
                </div>
                <h3 className="text-2xl font-bold mb-2 text-slate-900">Marine & Nautical</h3>
                <p className="text-slate-600 mb-6">Boat flags, nautical signals, and marine accessories</p>
                <Button asChild className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700">
                  <Link to="/marine">Shop Now</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border-2 border-transparent hover:border-blue-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-blue-700" />
                </div>
                <h3 className="text-2xl font-bold mb-2 text-slate-900">Military Flags</h3>
                <p className="text-slate-600 mb-6">Honor service members with marine-grade military flags</p>
                <Button asChild className="bg-blue-700 hover:bg-blue-800">
                  <Link to="/military">Shop Now</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border-2 border-transparent hover:border-blue-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-slate-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Wrench className="w-8 h-8 text-slate-700" />
                </div>
                <h3 className="text-2xl font-bold mb-2 text-slate-900">Poles & Hardware</h3>
                <p className="text-slate-600 mb-6">Hurricane-resistant poles and marine-grade hardware</p>
                <Button asChild className="bg-slate-700 hover:bg-slate-800">
                  <Link to="/poles">Shop Now</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Coastal Services */}
      <section className="relative py-20 overflow-hidden">
        {/* Stars pattern background */}
        <div 
          className="absolute inset-0 opacity-8"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(59, 130, 246, 0.3) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-white via-blue-50/40 to-red-50/40"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Flagpole Installation, Repair, Rerope, Service &amp; Sales</h2>
            <p className="text-xl text-slate-600">Professional flagpole installation, repair, rerope, service &amp; flag/flagpole sales - Gulf Coast Florida</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
            <Card className="bg-gradient-to-br from-blue-900 to-cyan-800 text-white hover:shadow-2xl transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4">Flagpole Installation</h3>
                <p className="text-blue-100 mb-6">
                  Professional flagpole installation service in Destin, 30A, Panama City Beach & the Gulf Coast. Residential & commercial flagpole installation.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-cyan-300" />
                    <span>Ground sleeve installation</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-cyan-300" />
                    <span>Hurricane-resistant systems</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-cyan-300" />
                    <span>Free installation quotes</span>
                  </li>
                </ul>
                <Button asChild variant="secondary" size="lg" className="w-full">
                  <Link to="/installation">Get Free Quote</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white hover:shadow-2xl transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4">Flagpole Rerope Service</h3>
                <p className="text-slate-300 mb-6">
                  Flagpole rerope & restringing service. We replace flagpole ropes, halyards & pulleys. Fast rerope service throughout Florida.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-cyan-300" />
                    <span>Halyard replacement</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-cyan-300" />
                    <span>Pulley repair & replace</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-cyan-300" />
                    <span>Marine-grade rope</span>
                  </li>
                </ul>
                <Button asChild variant="secondary" size="lg" className="w-full">
                  <Link to="/installation">Schedule Rerope</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-cyan-600 to-blue-700 text-white hover:shadow-2xl transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4">Flagpole Repair & Service</h3>
                <p className="text-cyan-100 mb-6">
                  Complete flagpole repair & maintenance service. We fix broken flagpoles, replace parts & provide ongoing flagpole service.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-white" />
                    <span>Flagpole repair</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-white" />
                    <span>Parts replacement</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-white" />
                    <span>Maintenance service</span>
                  </li>
                </ul>
                <Button asChild variant="secondary" size="lg" className="w-full">
                  <Link to="/installation">Request Service</Link>
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Service Areas for SEO */}
          <div className="text-center bg-white/80 backdrop-blur rounded-xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Service Areas</h3>
            <p className="text-slate-600 mb-4">
              Flagpole installation, rerope & repair service available in:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {['Destin', 'Santa Rosa Beach', '30A', 'Panama City Beach', 'Panama City', 'Miramar Beach', 'Fort Walton Beach', 'Niceville', 'Navarre', 'Sandestin'].map((area) => (
                <span key={area} className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-medium">
                  {area}
                </span>
              ))}
            </div>
            <p className="mt-6 text-lg">
              <a href="tel:205-587-2402" className="text-blue-600 font-bold hover:underline">
                📞 Call 205-587-2402 for Free Quotes
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Past Projects */}
      <section className="relative py-20 overflow-hidden">
        {/* Diagonal stripes pattern */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(220, 38, 38, 0.4) 35px, rgba(220, 38, 38, 0.4) 70px, transparent 70px, transparent 105px, rgba(59, 130, 246, 0.4) 105px, rgba(59, 130, 246, 0.4) 140px)',
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-red-50/30 to-blue-50/30"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Gulf Coast Projects</h2>
            <p className="text-xl text-slate-600">Professional installations across the Florida Panhandle</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pastProjects.map((project) => (
              <Card key={project.id} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 border-transparent hover:border-blue-300">
                <div className="relative overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-56 object-contain bg-slate-100"
                    data-testid={`project-image-${project.id}`}
                  />
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg mb-1 text-slate-900">{project.title}</h3>
                  <p className="text-sm text-blue-600 mb-2">{project.location}</p>
                  <p className="text-sm text-slate-600">{project.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 overflow-hidden">
        {/* American Flag Background */}
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1551893478-c716cec0de9e?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 to-cyan-900/95"></div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready to Display Your Colors?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contact us today for expert coastal flag solutions. Mobile service available throughout the Gulf Coast.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-6 text-lg">
              <a href="tel:205-587-2402">Call: 205-587-2402</a>
            </Button>
            <Button asChild variant="outline" size="lg" className="px-8 py-6 text-lg border-2 border-white text-white hover:bg-white hover:text-blue-900">
              <Link to="/installation">Get Free Quote</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CoastalHome;
